import React, { FC, useState } from 'react';

import CloseIcon from '@material-ui/icons/Close';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import ReactDOM from 'react-dom';

import { useDebounce, useI18n, useSelector } from 'Hooks';

import { ImageData } from '..';
import getImageUrlWithSize from '../../../../utils/getResizedImage';
import Loader from '../../../Loader';
import SearchInput from '../../SearchInput';
import ImageCard from '../ImageCard';
import ImageDrop from '../ImageDrop';
import { getAllImages } from './__queries__';
import {
  Background,
  Content,
  ContentWrapper,
  DropWrapper,
  Header,
  InsertButton,
  SortBlock,
  SortItem,
  SubTitle,
  Title,
  TopWrapper,
  Wrapper,
} from './styles';

const GET_IMAGES = gql`
  query getAllImages($companyId: ID!, $orderBy: ImageOrderByInput, $titleContains: String) {
    images: allImages(
      filter: { company: { id: $companyId }, title_contains: $titleContains }
      orderBy: $orderBy
    ) {
      id
      title
      updatedAt
      file {
        id
        url
      }
    }
  }
`;

export interface Props {
  onSelectImg: (image: ImageData) => void;
  onClose: () => void;
}

const Modal: FC<Props> = ({ onSelectImg, onClose }) => {
  const { t } = useI18n();
  const companyId = useSelector((state) => state.company);
  const [activeFilter, setActiveFilter] = useState('latest');
  const [searchValue, setSearchValue] = useState('');
  const [activeImage, setActiveImage] = useState<ImageData | null>(null);
  const debouncedSearch = useDebounce(searchValue, 300);

  const getOrderBy = () => {
    switch (activeFilter) {
      case 'latest':
        return 'updatedAt_DESC';
      case 'oldest':
        return 'updatedAt_ASC';
      case 'alphabetical':
        return 'title_ASC';
      default:
        return 'updatedAt_DESC';
    }
  };

  const domBody = document.querySelector('body');

  const { data, loading, refetch } = useQuery<getAllImages>(GET_IMAGES, {
    fetchPolicy: 'network-only',
    variables: {
      companyId,
      orderBy: getOrderBy(),
      titleContains: debouncedSearch,
    },
  });

  return domBody
    ? ReactDOM.createPortal(
        <div>
          <Background onClick={onClose} />
          <Wrapper>
            <TopWrapper>
              <Header>
                <div>
                  <Title>{t('PIM.ImageLibrary')}</Title>
                  <SubTitle>{t('PIM.SizeRecommendations')}</SubTitle>
                </div>
                <DropWrapper>
                  {/* @ts-ignore */}
                  <ImageDrop onSuccess={refetch} />
                </DropWrapper>
                <CloseIcon onClick={onClose} />
              </Header>
              <SearchInput
                placeholder={t('PIM.SearchFor')}
                value={searchValue}
                onChange={setSearchValue}
              />
              <SortBlock>
                <SortItem isDark>{t('PIM.Sort')}</SortItem>
                <SortItem
                  isDark={activeFilter === 'latest'}
                  withUnderline={activeFilter === 'latest'}
                  onClick={() => setActiveFilter('latest')}>
                  {t('General.Latest')}
                </SortItem>
                <SortItem
                  isDark={activeFilter === 'oldest'}
                  withUnderline={activeFilter === 'oldest'}
                  onClick={() => setActiveFilter('oldest')}>
                  {t('General.Oldest')}
                </SortItem>
                <SortItem
                  isDark={activeFilter === 'alphabetical'}
                  withUnderline={activeFilter === 'alphabetical'}
                  onClick={() => setActiveFilter('alphabetical')}>
                  {t('PIM.Alphabetical')}
                </SortItem>
              </SortBlock>
            </TopWrapper>
            <ContentWrapper>
              {loading ? (
                <Loader />
              ) : (
                <Content>
                  {data?.images.map((imageItem) => (
                    <ImageCard
                      key={imageItem.id}
                      onSelectImg={() => setActiveImage(imageItem)}
                      isActive={imageItem.id === activeImage?.id}
                      name={imageItem.title}
                      date={imageItem.updatedAt}
                      imageSrc={getImageUrlWithSize(imageItem.file.url, 180, 120)}
                    />
                  ))}
                  <InsertButton
                    onClick={activeImage ? () => onSelectImg(activeImage) : undefined}
                    isDisabled={!activeImage}>
                    {t('PIM.InsertImage')}
                  </InsertButton>
                </Content>
              )}
            </ContentWrapper>
          </Wrapper>
        </div>,
        domBody,
      )
    : null;
};

export default Modal;
